import { useCallback, useEffect, useState } from "react";
import {
  UserRound,
  Pencil,
  X,
  Check,
  Trash2,
  Loader2,
  Plus,
  Sparkles,
  UserPlus,
} from "lucide-react";
import {
  fetchUserModels,
  updateModelName,
  deleteModel,
  type ModelV2,
} from "../pages/v2/modelsApi";
import ProductGridSkeleton from "./ProductGridSkeleton";
import CachedImg from "./CachedImg";
import { SheetModosModelo } from "./SheetModosProducto";
import {
  AddOptionCard,
  useOpcionesMenu,
} from "../pages/v2/components/AddOptionCard";
import { ModelPickerSheet } from "../pages/v2/ModelPicker";
import { supabase } from "../lib/supabase";

/**
 * Tab "Mis Modelos" de la pantalla Mi marca: grilla de los modelos visibles
 * para el usuario (globales de Atlas + privados de su empresa).
 *
 * Los modelos PROPIOS (idCompany !== null) son editables: un botón de lápiz en
 * la esquina inferior derecha (visible al hover en desktop, siempre en mobile)
 * entra en modo edición, donde se puede renombrar el modelo o eliminarlo. Los
 * modelos globales de Atlas son solo lectura.
 */
export const MiMarcaModelsTab = ({
  companyId,
  rubro,
}: {
  companyId: string | null;
  /** Rubro de la marca activa — decide si el sheet de modos ofrece Plantillas. */
  rubro: string | null;
}) => {
  const [models, setModels] = useState<ModelV2[]>([]);
  const [loading, setLoading] = useState(true);
  // Modelo tocado en la grilla → sheet "¿Qué querés generar?"
  // (ADR-2026-08-04-desde-un-modelo-se-elige-el-modo).
  const [modeloParaGenerar, setModeloParaGenerar] = useState<ModelV2 | null>(
    null,
  );
  // "Agregar modelo": abre ACÁ MISMO el ModelPickerSheet de generación,
  // directo en la vista elegida (create/upload) — los flujos de creación son
  // los ya hechos, sin navegar a otra pantalla. El sheet va por portal a
  // body con z-[210] así que no lo tapa el layout de Mi marca.
  const [pickerVista, setPickerVista] = useState<"create" | "upload" | null>(
    null,
  );
  // El sheet necesita el userId (dueño del modelo creado/subido).
  const [userId, setUserId] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => {
      if (!cancelled) setUserId(data.user?.id ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const opcionesAgregarModelo = [
    {
      icon: <Sparkles className="w-3.5 h-3.5" />,
      label: "Creá tu modelo",
      onClick: () => setPickerVista("create"),
    },
    {
      icon: <UserPlus className="w-3.5 h-3.5" />,
      label: "Cargá tu propio modelo",
      onClick: () => setPickerVista("upload"),
    },
  ];
  // Menú del botón mobile — el MISMO popover que abre la card de desktop.
  const menuAgregarMobile = useOpcionesMenu(opcionesAgregarModelo);

  // ── Edición inline ───────────────────────────────────────────────────
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draftName, setDraftName] = useState("");
  const [saving, setSaving] = useState(false);
  // Confirmación de borrado (id del modelo a confirmar) + estado de borrado.
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Error de la operación activa (guardar / eliminar).
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setLoading(true);
    fetchUserModels(companyId)
      .then((m) => {
        // "Mis modelos" muestra SOLO los propios de la empresa. Los globales de
        // Atlas (idCompany === null) viven en el picker de generación, no acá.
        if (!cancelled) setModels(m.filter((x) => x.idCompany !== null));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // Refresh SIN skeleton (para cuando el sheet crea/sube un modelo: la grilla
  // se actualiza en el lugar, sin parpadeo de carga).
  const recargar = useCallback(() => {
    if (!companyId) return;
    fetchUserModels(companyId).then((m) =>
      setModels(m.filter((x) => x.idCompany !== null)),
    );
  }, [companyId]);

  const startEdit = (m: ModelV2) => {
    setEditingId(m.id);
    setDraftName(m.nombre);
    setConfirmDeleteId(null);
    setError(null);
  };

  const exitEdit = () => {
    setEditingId(null);
    setConfirmDeleteId(null);
    setDraftName("");
    setError(null);
  };

  const save = async (m: ModelV2) => {
    const name = draftName.trim();
    // Sin cambios (o vacío) → simplemente salimos del modo edición.
    if (!name || name === m.nombre) {
      exitEdit();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateModelName(m.id, name);
      setModels((prev) =>
        prev.map((x) => (x.id === m.id ? { ...x, nombre: name } : x)),
      );
      exitEdit();
    } catch (e) {
      console.error("[MiMarcaModelsTab] updateModelName error", e);
      setError("No se pudo guardar el nombre. Probá de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async (m: ModelV2) => {
    setDeleting(true);
    setError(null);
    try {
      await deleteModel(m);
      setModels((prev) => prev.filter((x) => x.id !== m.id));
      setConfirmDeleteId(null);
      setEditingId(null);
    } catch (e) {
      console.error("[MiMarcaModelsTab] deleteModel error", e);
      setError("No se pudo eliminar el modelo. Probá de nuevo.");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <ProductGridSkeleton
        count={9}
        gridClassName="grid grid-cols-2 xl:grid-cols-3 gap-3"
        tileAspectClassName="aspect-[9/16]"
        tileClassName="rounded-xl"
        withLabel
      />
    );
  }

  // Mobile: botón "Agregar modelo" arriba de la grilla — misma forma que el
  // "Agregar producto" del tab de productos, pero en blanco. Abre el mismo
  // menú (crear / cargar) que la card de desktop.
  const botonAgregarMobile = (
    <div className="sm:hidden mb-4">
      <button
        type="button"
        onClick={menuAgregarMobile.toggle}
        className="w-full inline-flex items-center justify-center gap-2 h-[var(--btn-h)] px-5 bg-white border border-neutral-200 hover:bg-neutral-50 text-brand-black/80 rounded-md shadow-sm text-[length:var(--text-body)] font-medium transition-colors"
      >
        <Plus className="w-4 h-4" />
        Agregar modelo
      </button>
      {menuAgregarMobile.menu}
    </div>
  );

  // Desktop: card "Agregar modelo" como un ítem más de la grilla, con la
  // misma UI de popover que los pickers de generación (AddOptionCard).
  const cardAgregarDesktop = (
    <div className="hidden sm:flex flex-col">
      <AddOptionCard
        label="Agregar modelo"
        rounded="rounded-xl"
        className="aspect-[9/16] w-full"
        options={opcionesAgregarModelo}
      />
    </div>
  );

  // Sheet del picker de modelos — los flujos de crear/cargar YA hechos,
  // abiertos acá mismo en la vista elegida. `selection`/`onChange` no aplican
  // (no se está eligiendo modelo para generar): no-op. Va en los DOS returns
  // (grilla y vacío) porque las opciones de agregar existen en ambos.
  const sheetAgregarModelo = (
    <ModelPickerSheet
      open={pickerVista !== null}
      selection={null}
      onChange={() => {}}
      onClose={() => setPickerVista(null)}
      userModels={models}
      userId={userId}
      companyId={companyId}
      onModelCreated={recargar}
      initialView={pickerVista ?? "create"}
      hideAuto
      lockView
    />
  );

  if (models.length === 0) {
    return (
      <div>
        {botonAgregarMobile}
        {/* Desktop: la card de agregar reemplaza al empty state (invita a la
            acción); en mobile el botón ya quedó arriba y el mensaje se queda. */}
        <div className="hidden sm:grid grid-cols-2 xl:grid-cols-3 gap-3">
          {cardAgregarDesktop}
        </div>
        <div className="sm:hidden flex flex-col items-center justify-center text-center py-16 text-brand-black/40">
          <UserRound className="w-8 h-8 mb-3" strokeWidth={1.5} />
          <p className="text-[14px]">Todavía no hay modelos.</p>
        </div>
        {sheetAgregarModelo}
      </div>
    );
  }

  return (
    <div>
      {botonAgregarMobile}
      <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
      {cardAgregarDesktop}
      {models.map((m) => {
        const isOwn = m.idCompany !== null;
        const isEditing = editingId === m.id;
        const isConfirming = confirmDeleteId === m.id;
        const dirty =
          isEditing &&
          draftName.trim() !== "" &&
          draftName.trim() !== m.nombre;

        return (
          <div key={m.id} className="flex flex-col">
            <div className="group relative aspect-[9/16] rounded-xl overflow-hidden bg-neutral-100">
              {m.thumbUrl ? (
                <CachedImg
                  src={m.thumbUrl}
                  alt={m.nombre}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-brand-black/25">
                  <UserRound className="w-6 h-6" strokeWidth={1.5} />
                </div>
              )}

              {/* Capa de click sobre la miniatura: abre el sheet de modos para
                  generar CON ESE modelo
                  (ADR-2026-08-04-desde-un-modelo-se-elige-el-modo). Mismo patrón que
                  la grilla de productos —botón absoluto y no la tarjeta entera,
                  porque adentro hay otros botones y un botón dentro de otro es HTML
                  inválido—. Los controles de edición van con `z-10`, por encima.
                  Mientras se renombra o se confirma un borrado no se monta: ahí el
                  tap es parte de esa operación, no un "generar con este modelo". */}
              {!isEditing && !isConfirming && (
                <button
                  type="button"
                  onClick={() => setModeloParaGenerar(m)}
                  aria-label={`Generar con ${m.nombre}`}
                  className="absolute inset-0 z-[1] cursor-pointer"
                />
              )}

              {/* Confirmación de borrado (overlay sobre la miniatura). */}
              {isConfirming && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2.5 bg-black/55 backdrop-blur-sm p-3 text-center animate-fade-in">
                  <p className="text-[12.5px] text-white font-medium leading-tight">
                    ¿Eliminar este modelo?
                  </p>
                  <div className="flex flex-col gap-1.5 w-full max-w-[140px]">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDelete(m);
                      }}
                      disabled={deleting}
                      className="w-full inline-flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-[12.5px] font-semibold disabled:opacity-50 transition-colors"
                    >
                      {deleting && (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      )}
                      {deleting ? "Eliminando…" : "Eliminar"}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDeleteId(null);
                        setError(null);
                      }}
                      disabled={deleting}
                      className="w-full py-1.5 rounded-lg bg-white/90 hover:bg-white text-brand-black/80 text-[12.5px] font-medium disabled:opacity-50 transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                  {error && (
                    <p className="text-[11px] text-red-200 leading-tight">
                      {error}
                    </p>
                  )}
                </div>
              )}

              {/* Controles de edición (solo modelos propios). En idle: lápiz
                  (hover en desktop, siempre en mobile). En edición: botón
                  principal (✕ cancelar / ✓ guardar) + botón eliminar arriba. */}
              {isOwn && !isConfirming && (
                <div className="absolute bottom-2 right-2 z-10 flex flex-col-reverse items-end gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isEditing) startEdit(m);
                      else if (dirty) save(m);
                      else exitEdit();
                    }}
                    disabled={saving}
                    aria-label={
                      !isEditing
                        ? "Editar modelo"
                        : dirty
                          ? "Guardar nombre"
                          : "Cancelar edición"
                    }
                    className={`inline-flex items-center justify-center w-8 h-8 rounded-full shadow-md ring-1 ring-black/[0.06] backdrop-blur-md transition-all active:scale-90 disabled:opacity-60 ${
                      isEditing && dirty
                        ? "bg-brand-primary text-white hover:bg-brand-primary-hover"
                        : "bg-white/95 text-brand-black/80 hover:bg-white"
                    } ${
                      isEditing
                        ? "opacity-100"
                        : "opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                    }`}
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : !isEditing ? (
                      <Pencil className="w-4 h-4" />
                    ) : dirty ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <X className="w-4 h-4" />
                    )}
                  </button>

                  {isEditing && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDeleteId(m.id);
                        setError(null);
                      }}
                      disabled={saving}
                      aria-label="Eliminar modelo"
                      className="animate-mode-swap inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/95 text-red-500 shadow-md ring-1 ring-black/[0.06] backdrop-blur-md transition-all active:scale-90 hover:bg-white disabled:opacity-60"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Nombre — input en modo edición, texto si no. */}
            {isEditing ? (
              <>
                <input
                  type="text"
                  value={draftName}
                  autoFocus
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (dirty) save(m);
                      else exitEdit();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      exitEdit();
                    }
                  }}
                  className="mt-1.5 w-full text-[12.5px] text-brand-black/90 leading-tight bg-white border border-neutral-200 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
                />
                {error && !isConfirming && (
                  <p className="mt-1 text-[11px] text-red-500 leading-tight">
                    {error}
                  </p>
                )}
              </>
            ) : (
              <p className="mt-1.5 text-[12.5px] text-brand-black/80 leading-tight line-clamp-2">
                {m.nombre}
              </p>
            )}
          </div>
        );
      })}
      </div>

      {/* Sheet de modos: el mismo drawer que la grilla de productos, pero sólo con
          los modos en los que se puede elegir un modelo. */}
      <SheetModosModelo
        modelo={modeloParaGenerar}
        rubro={rubro}
        onClose={() => setModeloParaGenerar(null)}
      />
      {sheetAgregarModelo}
    </div>
  );
};
